import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { addDays, differenceInCalendarDays, e4Format, isPast } from 'helpers/dates';

import { taskStatuses, taskStatus } from 'helpers/profile';
import { loadTasks } from 'actions/profile';
import Rating from 'components/general/rating';
import NoResults from 'components/general/noResults';

import { Box, Help, Loading, Paging, ArrayHelpers, SystemText } from 'e4shared';
import TaskIcon from 'views/user/task/components/taskIcon';

import './taskList.css';

export const RatingHelp = task => {
    const hasCompetenciesWithRatings =
        ArrayHelpers.existsAndHasLength(task?.competencies) && task.competencies.filter(c => c.rating > 0)?.length > 0;

    if (!hasCompetenciesWithRatings) return null;

    return (
        <Help id={`rating-task${task.id}`}>
            <span>
                <b>{SystemText('Tasks/TaskList/Help/Rating')}</b>
                {task.competencies.map((c, i) => {
                    return (
                        <span key={`breakdown-${i}`}>
                            <br />
                            {`${c.area} - ${c.name} - ${c.rating}`}
                        </span>
                    );
                })}
            </span>
        </Help>
    );
};

export const taskBadgeColour = task => {
    if (task?.date_Completed) return '';
    return isPast(task?.due)
        ? 'label table-label label-danger'
        : isPast(addDays(task.due, -14))
        ? 'label table-label label-warning'
        : '';
};

export const taskRating = (task, competencyId, hideNa) => {
    let res = 0;
    if (ArrayHelpers.existsAndHasLength(task?.competencies)) {
        if (competencyId) {
            const elems = task.competencies.filter(c => c.id === competencyId);
            res = elems.length ? elems[0].rating : 0;
        } else {
            const avg = task.competencies.reduce((a, b) => a + b?.rating, 0) / task.competencies.length;
            const avgFixed = avg % 1 === 0 ? avg : avg.toFixed(1);
            res = avgFixed;
        }
    } else {
        res = task.rating;
    }

    return res === 0 ? (
        hideNa ? null : (
            SystemText('Tasks/TaskList/Summary/NA')
        )
    ) : (
        <span className="inline-rating" style={{ minWidth: '95px' }}>
            <Rating hideLabel smallStar value={res} />
        </span>
    );
};

export const TaskList = ({
    history,
    profileId,
    filter,
    competencyId,
    summary,
    compRating,
    lastRefreshed,
    renderOutOfBox,
}) => {
    const dispatch = useDispatch();

    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(false);

    const tasks = useSelector(state => state.profile.taskList.tasks);
    const records = useSelector(state => state.profile.taskList.records);

    const goToTask = task => {
        if (task?.appraisal_Id) {
            history.push(`/develop/reviews/${task.appraisal_Id}?oid=${task.date_Completed ? '' : task.id}`);
        } else {
            history.push(`/objectives/${profileId}/objective/${task.id}`);
        }
    };

    useEffect(() => {
        dispatch(loadTasks(profileId, 1, filter, competencyId));
    }, [dispatch, profileId, filter, competencyId, lastRefreshed]);

    const gotoPage = page => {
        setPage(page);
        setLoading(true);
        dispatch(loadTasks(profileId, page, filter, competencyId, () => setLoading(false)));
    };

    if (!tasks || loading)
        return (
            <span data-testid="tasklist-loading-testid">
                <Loading />
            </span>
        );

    const isShared = filter === 'shared';

    const tableFooter = (
        <div className="text-center mt10">
            <Paging goToPage={page => gotoPage(page)} recordCount={records} page={page} />
        </div>
    );

    let cols = 2;
    const setCols = () => {
        cols++;
        return true;
    };

    const getOriginator = task => {
        if (task.course_Id) return task.course;
        else if (task.owner_Fullname) return task.owner_Fullname;
        else return 'System';
    };

    const renderTaskRows = tasks => {
        return tasks?.map(task => {
            const status = taskStatus(task);
            const diff = task.date_Completed ? differenceInCalendarDays(task.due, task.date_Completed) : 0;
            const originator = getOriginator(task);

            return (
                <tr data-testid="tasklist-tablerow-testid" key={task.id} onClick={() => goToTask(task)} className="pointer">
                    {isShared && <td>{task.assigned_Fullname}</td>}
                    <td>
                        {task.pathway_Id && <span className="mr10">-</span>}
                        {task.name}
                        <TaskIcon icons={task.icons} />
                        {task.description && <Help id={`row_${task.id}`}>{task.description}</Help>}
                        {summary && (
                            <div className="mt5">
                                <div className={`summary-due ${taskBadgeColour(task)}`}>
                                    {SystemText('Tasks/TaskList/Summary/Due')}: {e4Format(task.due)}
                                </div>
                            </div>
                        )}
                    </td>
                    {summary && status === taskStatuses.Completed && (
                        <td>
                            <span className="label label-success">{SystemText('Tasks/TaskList/Status/Completed')}</span>
                        </td>
                    )}
                    {summary && status === taskStatuses.AwaitingApproval && (
                        <td>
                            <span className="label label-warning">
                                {SystemText('Tasks/TaskList/Status/AwaitingApproval')}
                            </span>
                        </td>
                    )}
                    {summary && status === taskStatuses.Overdue && (
                        <td>
                            <span className="label label-danger">{SystemText('Tasks/TaskList/Status/Overdue')}</span>
                        </td>
                    )}
                    {summary && status === taskStatuses.Outstanding && (
                        <td>
                            <span className="label label-default">{SystemText('Tasks/TaskList/Status/Outstanding')}</span>
                        </td>
                    )}
                    {!summary && (
                        <td>
                            <span>{e4Format(task.date_Assigned)}</span>
                        </td>
                    )}
                    {!summary && (
                        <td>
                            <span className={` no-wrap ${taskBadgeColour(task)}`}>{e4Format(task.due)}</span>
                        </td>
                    )}
                    {!summary && (
                        <td>
                            {task.date_Completed ? (
                                <span className="flex flex-center">
                                    <div className="no-wrap">{e4Format(task.date_Completed)}</div>
                                    {task.metric_Task && !task.goal_Met ? (
                                        <div className="p5 ml10 hidden-xs hidden-sm hidden-md label label-danger">
                                            <i className="fa fa-times fa-red mr5" />
                                            <span>{SystemText('Tasks/TaskList/Summary/NotAchieved')}</span>
                                        </div>
                                    ) : diff < 0 ? (
                                        <div className="p5 ml10 hidden-xs hidden-sm hidden-md label label-danger">
                                            <i className="fa fa-warning fa-red mr5" />
                                            <span>{SystemText('Tasks/TaskList/Summary/Late')}</span>
                                        </div>
                                    ) : (
                                        <div className="p5 ml10 hidden-xs hidden-sm hidden-md label label-success">
                                            <i className="fa fa-thumbs-up fa-green mr5" />
                                            <span>{SystemText('Tasks/TaskList/Summary/OnTime')}</span>
                                        </div>
                                    )}
                                </span>
                            ) : (
                                '-'
                            )}
                        </td>
                    )}
                    {!summary && !isShared && (
                        <td>{task.pathway_Id ? SystemText('Tasks/TaskList/Originator/NotApplicable') : originator}</td>
                    )}
                    {compRating && (
                        <td className="rating-col">
                            <span className="pull-right flex">{taskRating(task, compRating ? competencyId : null)}</span>
                        </td>
                    )}
                </tr>
            );
        });
    };

    const mainContent = (
        <div data-testid="tasklist-wrapper-testid" className="table-responsive">
            <table className="table table-hover mb10" data-testid="objectives-table">
                <thead>
                    <tr>
                        {isShared && setCols() && <th>{SystemText('Tasks/TaskList/Columns/Assignee')}</th>}
                        <th width={summary ? null : '50%'}>{SystemText('Tasks/TaskList/Columns/Objective')}</th>
                        {summary && setCols() && <th>{SystemText('Tasks/TaskList/Columns/Status')}</th>}
                        {!summary && setCols() && <th>{SystemText('Tasks/TaskList/Summary/Assigned')}</th>}
                        {!summary && setCols() && <th>{SystemText('Tasks/TaskList/Summary/Due')}</th>}
                        {!summary && setCols() && <th>{SystemText('Tasks/TaskList/Status/Completed')}</th>}
                        {!summary && !isShared && setCols() && <th>{SystemText('Tasks/TaskList/Columns/Originator')}</th>}
                        {compRating && setCols() && (
                            <th className="text-right">{SystemText('Tasks/TaskList/Columns/Score')}</th>
                        )}
                    </tr>
                </thead>
                <tbody>
                    {!tasks?.length && (
                        <tr>
                            <td data-testid="tasklist-noresults-testid" colSpan={cols} className="td-no-results">
                                <NoResults
                                    title={SystemText('Tasks/TaskList/NoResults/Title')}
                                    message={SystemText('Tasks/TaskList/NoResults/Message')}
                                />
                            </td>
                        </tr>
                    )}
                    {renderTaskRows(tasks)}
                </tbody>
            </table>
        </div>
    );

    return renderOutOfBox ? [mainContent, tableFooter] : <Box footer={tableFooter}>{mainContent}</Box>;
};

export default withRouter(TaskList);
